import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Reveal } from "../animations/Reveal";
import { designTokens } from '../../theme/designTokens';
import { Link } from "react-router-dom";
import {
  Instagram,
  Facebook,
  Twitter,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Truck,
  Shield,
  Sprout,
  ArrowUp,
  Globe,
  Heart,
  Circle
} from 'lucide-react';

const Footer = () => {
  const [hoveredLink, setHoveredLink] = useState(null);
  const [circles, setCircles] = useState([]);

  const footerLinks = {
    Shop: [
      { label: "Skincare", path: "/shop/skincare" },
      { label: "Serums", path: "/shop/serums" },
      { label: "Rituals", path: "/shop/rituals" },
      { label: "Collections", path: "/collections" },
      { label: "Gift Sets", path: "/gift-sets" },
    ],
    About: [
      { label: "Our Story", path: "/about" },
      { label: "Philosophy", path: "/philosophy" },
      { label: "Sustainability", path: "/sustainability" },
      { label: "Ingredients", path: "/ingredients" },
      { label: "Journal", path: "/journal" },
    ],
    Support: [
      { label: "Contact Us", path: "/contact" },
      { label: "Shipping", path: "/shipping" },
      { label: "Returns", path: "/returns" },
      { label: "FAQ", path: "/faq" },
      { label: "Store Locator", path: "/stores" },
    ],
    Legal: [
      { label: "About Us", path: "/about" },
      { label: "Privacy Policy", path: "/privacy-policy" },
      { label: "Terms of Service", path: "/terms" },
      { label: "Accessibility", path: "/accessibility" },
      { label: "Cookies", path: "/cookies" },
    ],
  };


  const socialLinks = [
    { icon: <Instagram size={20} />, label: 'Instagram', color: '#E4405F' },
    { icon: <Facebook size={20} />, label: 'Facebook', color: '#1877F2' },
    { icon: <Twitter size={20} />, label: 'Twitter', color: '#1DA1F2' }
  ];

  const trustBadges = [
    { icon: <Shield size={20} />, text: 'Clean & Safe', color: designTokens.colors.sage },
    { icon: <Sprout size={20} />, text: 'Carbon Neutral', color: designTokens.colors.sage },
    { icon: <Truck size={20} />, text: 'Free Shipping', color: designTokens.colors.champagne },
    { icon: <CreditCard size={20} />, text: 'Secure Payment', color: designTokens.colors.champagne }
  ];

  const paymentMethods = ['Visa', 'Mastercard', 'Amex', 'PayPal'];

  useEffect(() => {
    const generatedCircles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      size: Math.random() * 40 + 20,
      x: Math.random() * 100,
      y: Math.random() * 100,
      color: Math.random() > 0.5 ? designTokens.colors.champagne : designTokens.colors.sage,
      opacity: Math.random() * 0.15 + 0.05,
      duration: Math.random() * 20 + 15,
      delay: Math.random() * 5,
    }));
    setCircles(generatedCircles);
  }, []);

  return (
      <footer className="bg-charcoal text-white relative overflow-hidden">
        {/* Animated Circles Background */}
        <div className="absolute inset-0 pointer-events-none z-0">
          {circles.map((circle) => (
              <motion.div
                  key={circle.id}
                  className="absolute rounded-full border"
                  style={{
                    left: `${circle.x}%`,
                    top: `${circle.y}%`,
                    width: `${circle.size}px`,
                    height: `${circle.size}px`,
                    borderColor: circle.color,
                    opacity: circle.opacity,
                  }}
                  animate={{
                    x: [0, Math.random() * 50 - 25, 0],
                    y: [0, Math.random() * 50 - 25, 0],
                    scale: [1, 1.2, 1],
                    borderWidth: ['1px', '2px', '1px'],
                  }}
                  transition={{
                    duration: circle.duration,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: circle.delay,
                  }}
              >
                <motion.div
                    className="absolute inset-1.5 rounded-full border opacity-60"
                    style={{ borderColor: `${circle.color}80` }}
                    animate={{
                      scale: [1, 0.6, 1],
                    }}
                    transition={{
                      duration: circle.duration / 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                />
              </motion.div>
          ))}

          {/* Large rotating circles */}
          <motion.div
              className="absolute top-10 left-5 w-20 h-20 border border-champagne/30 rounded-full hidden lg:block"
              animate={{
                rotate: 360,
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 25,
                repeat: Infinity,
                ease: "linear",
              }}
          />

          <motion.div
              className="absolute bottom-15 right-5 w-16 h-16 border border-sage/30 rounded-full hidden lg:block"
              animate={{
                rotate: -360,
                scale: [1.1, 1, 1.1],
              }}
              transition={{
                duration: 30,
                repeat: Infinity,
                ease: "linear",
                delay: 10,
              }}
          />

          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-charcoal via-transparent to-charcoal opacity-80" />
        </div>

        {/* Trust Badges Section */}
        <div className="border-b border-white/10 relative z-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <Reveal>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 text-center">
                {trustBadges.map((badge, index) => (
                    <Reveal key={badge.text} delay={index * 0.1}>
                      <motion.div
                          className="flex items-center justify-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white/5 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-white/10 hover:border-current"
                          style={{ '--hover-color': badge.color }}
                          whileHover={{
                            y: -5,
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          }}
                          transition={{ duration: 0.3 }}
                      >
                        <motion.div
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 flex items-center justify-center border flex-shrink-0"
                            style={{ borderColor: `${badge.color}40` }}
                            whileHover={{ rotate: 360 }}
                            transition={{ duration: 0.6 }}
                        >
                          <div style={{ color: badge.color }} className="scale-90 sm:scale-100">
                            {badge.icon}
                          </div>
                        </motion.div>
                        <span className="text-xs sm:text-sm font-medium">
                          {badge.text}
                        </span>
                      </motion.div>
                    </Reveal>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-16 relative z-10">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-6 gap-6 sm:gap-8 lg:gap-12 mb-8 sm:mb-12">
            {/* Brand Column - Takes 2 columns on mobile, 2 columns on desktop */}
            <div className="col-span-2 sm:col-span-2 lg:col-span-2 space-y-4 sm:space-y-6">
              <Reveal>
                <div className="flex items-center gap-3 mb-2">
                  <motion.div
                      className="w-1 h-8 bg-gradient-to-b from-champagne to-sage rounded"
                      animate={{
                        height: ['2rem', '2.5rem', '2rem'],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                  />
                  <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-light tracking-tight">
                    KROMAIC
                  </h3>
                  <motion.div
                      className="text-champagne opacity-50 hidden sm:block"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  >
                    <Circle size={20} />
                  </motion.div>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <p className="text-xs sm:text-sm text-white/60 leading-relaxed">
                  Where botanical science meets emotional wellness.
                  Luxury skincare for the conscious individual.
                </p>
              </Reveal>

              <Reveal delay={0.4}>
                <div className="flex items-center gap-3 sm:gap-4">
                  {socialLinks.map((social) => (
                      <motion.a
                          key={social.label}
                          href="#"
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/5 flex items-center justify-center border border-white/10 relative overflow-hidden hover:border-current"
                          style={{ '--hover-color': social.color }}
                          whileHover={{
                            y: -3,
                            scale: 1.1,
                          }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ duration: 0.3 }}
                          aria-label={social.label}
                          onMouseEnter={() => setHoveredLink(social.label)}
                          onMouseLeave={() => setHoveredLink(null)}
                      >
                        <div className={`transition-colors relative z-10 ${hoveredLink === social.label ? 'text-current' : 'text-white/70'}`}>
                          {React.cloneElement(social.icon, { size: 16 })}
                        </div>
                      </motion.a>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* Link Columns - Each takes 1 column on mobile, 1 column on desktop */}
            {Object.entries(footerLinks).map(([category, links], categoryIndex) => (
                <Reveal key={category} delay={categoryIndex * 0.1}>
                  <div className="space-y-3 sm:space-y-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-white/40 flex items-center gap-2 pb-2 sm:pb-3 border-b border-white/10">
                      <Circle size={8} />
                      {category}
                    </h4>

                    <ul className="space-y-2 sm:space-y-3">
                      {links.map((link, linkIndex) => (
                          <motion.li
                              key={link.path}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{
                                delay: categoryIndex * 0.1 + linkIndex * 0.05,
                                duration: 0.5,
                              }}
                          >
                            <motion.div
                                whileHover={{ x: 5 }}
                                transition={{ duration: 0.3 }}
                            >
                              <Link
                                  to={link.path}
                                  className="group text-xs sm:text-sm text-white/70 hover:text-champagne transition-colors flex items-center gap-2 sm:gap-3 py-1"
                              >
                                <span className="w-1.5 h-1.5 bg-champagne rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                                {link.label}
                              </Link>
                            </motion.div>
                          </motion.li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
            ))}
          </div>

          {/* Contact Info */}
          <Reveal delay={0.6}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 pt-8 sm:pt-12 border-t border-white/10">
              {[
                {
                  icon: <Mail size={16}  />,
                  title: 'Email',
                  content: 'contact@kromaic.com',
                  color: designTokens.colors.champagne
                },
                {
                  icon: <Phone size={16}  />,
                  title: 'Phone',
                  content: '+1 (888) 567-4321',
                  color: designTokens.colors.sage
                },
                {
                  icon: <MapPin size={16}  />,
                  title: 'Flagship Stores',
                  content: 'Paris • Tokyo • New York',
                  icon2: <Globe size={12} />,
                  color: designTokens.colors.white
                },
              ].map((item, index) => (
                  <motion.div
                      key={index}
                      className="flex items-center gap-3 sm:gap-4"
                      whileHover={{ x: 5 }}
                  >
                    <motion.div
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/5 flex items-center justify-center border border-white/10 relative overflow-hidden flex-shrink-0"
                        whileHover={{
                          rotate: 360,
                          borderColor: item.color,
                        }}
                        transition={{ duration: 0.6 }}
                    >
                      <div className="text-current relative z-10" style={{ color: item.color }}>
                        {item.icon}
                      </div>
                    </motion.div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-white/40 mb-1">
                        {item.title}
                      </div>
                      <div className="text-sm text-white flex items-center gap-2">
                        {item.icon2 && item.icon2}
                        {item.content}
                      </div>
                    </div>
                  </motion.div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 bg-black/30 relative z-10 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <Reveal>
              <div className="flex flex-col items-center gap-3 sm:gap-4 text-center">
                <div className="text-xs sm:text-sm text-white/40 flex items-center gap-1 sm:gap-2 flex-wrap justify-center">
                  <Circle size={10} />
                  <span>© {new Date().getFullYear()} KROMAIC. All rights reserved.</span>
                  <Circle size={6}  className="opacity-30" />
                  <span>Made with <Heart size={12}  className="inline mx-1" /> for conscious beauty</span>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 justify-center">
                  <span className="text-xs text-white/40">
                    Payment Methods:
                  </span>
                  <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
                    {paymentMethods.map((method) => (
                        <motion.div
                            key={method}
                            whileHover={{ y: -2 }}
                        >
                          <span className="text-xs sm:text-sm text-white/60 px-2 sm:px-3 py-1 sm:py-2 bg-white/5 rounded-full border border-white/10 hover:bg-white/10 transition-colors">
                            {method}
                          </span>
                        </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Back to Top Button */}
        <motion.button
            className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-champagne to-sage text-charcoal border-none cursor-pointer flex items-center justify-center shadow-lg z-100"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            whileHover={{
              y: -5,
              scale: 1.1,
              boxShadow: '0 8px 30px rgba(201, 162, 77, 0.4)',
            }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 1,
              duration: 0.5,
              ease: "easeOut"
            }}
            aria-label="Back to top"
        >
          <motion.div
              animate={{
                y: [0, -2, 0],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
          >
            <ArrowUp size={20}  />
          </motion.div>
        </motion.button>
      </footer>
  );
};

export default Footer;